import moment, { Moment } from 'moment';
import { Formats } from 'shovel-lib/types';
import t from '../i18n/t';

/**
 * How a task's due date and start date read, everywhere in the app (Petr, 2026-10-19): "Today", "Tomorrow", the weekday
 * for the coming 6 days (only one Thursday is coming, and students live by their weekly classes), then the date. Back
 * in time: "Yesterday", "3 days ago", then the date. Two sizes:
 * - short, for cards and list cells: "Today 13:00", "Thu 10:00", "Thu" (23:59 goes without saying), "Oct 28" (7+ days
 *   out, no time: it matters once the day is near);
 * - long, for popups and headings: a strong part and a soft one after a dot, "Thursday 10:00 · Oct 22",
 *   "Wednesday, Oct 28, 13:00 · in 9 days", "2 days ago · Sat, Oct 17".
 * A deadline at 00:00 belongs to the day before ("Tuesday midnight"), where the calendar draws it.
 */

type DateInput = string | Date | Moment;

/** The app's own format for a token once the student's settings have loaded ("7 Oct", "13:00"); until then, `fallback`. */
export const inAppFormat = (format: Formats, fallback: string) =>
  (moment.localeData() as any).longDateFormat(format) ? format : fallback;

const shortDateFormat = () => inAppFormat(Formats.MONTH_DAY_FORMAT, 'MMM D');
const timeFormat = () => inAppFormat(Formats.TIME, 'h:mma');

const isMidnight = (at: Moment) => at.hours() === 0 && at.minutes() === 0;

/** 23:59 or midnight: the end of a day, which needs no time on a card. */
export const isEndOfDay = (at: Moment) => isMidnight(at) || (at.hours() === 23 && at.minutes() === 59);

/** The day a deadline belongs to, at its start: a 00:00 deadline is the end of the day before. */
export const deadlineDay = (date: DateInput) => {
  const at = moment(date);
  return (isMidnight(at) ? at.clone().subtract(1, 'day') : at.clone()).startOf('day');
};

/** Whole days from today to `day` (negative: past). */
const daysFrom = (day: Moment, now: Moment) => Math.round(day.diff(now.clone().startOf('day'), 'days', true));

/** "Oct 22", or "Jan 5 '27" in another year. */
const formatDate = (day: Moment, now: Moment) =>
  `${day.format(shortDateFormat())}${day.year() === now.year() ? '' : ` '${day.format('YY')}`}`;

const formatTime = (at: Moment) => (isMidnight(at) ? t.MIDNIGHT : at.format(timeFormat()));

/**
 * The day by itself: Today, Tomorrow, Thu/Thursday, Yesterday, 3 days ago, else the date. With `weekdays`, every day of
 * the week around today goes by its weekday, as the calendar's columns name them.
 */
const dayWord = (days: number, day: Moment, now: Moment, weekday: 'ddd' | 'dddd', weekdays = false) => {
  if (weekdays && days > -7 && days < 7) return day.format(weekday);
  if (days === 0) return t.TODAY;
  if (days === 1) return t.TOMORROW;
  if (days === -1) return t.YESTERDAY;
  if (days > 1 && days < 7) return day.format(weekday);
  if (days < -1 && days > -7) return t.DAYS_AGO(-days);
  return formatDate(day, now);
};

export type DayLabel = {
  /** Cards and list cells: "Today 13:00", "Thu", "Oct 28". */
  short: string;
  /** Popups and headings, in strong type: "Thursday 10:00", "Wednesday, Oct 28, 13:00", "2 days ago". */
  strong: string;
  /** What goes after it, softer: the date ("Oct 22"), or how far ("in 9 days") when the strong part is a date. */
  soft: string;
  /** Whole days from today to the deadline's day. */
  days: number;
  /** The exact time has passed. */
  overdue: boolean;
};

type Options = {
  now?: Moment;
  /** Leave the time out of the long size (sidebar headings, where every card below gives its own). */
  withoutTime?: boolean;
  /** Give the time in the short size too, even 23:59 and far out: a date being set ("Thu 23:59", "Oct 28, 23:59"). */
  keepTime?: boolean;
  /**
   * Name today, tomorrow and the past week's days by their weekday too, in the short size: the calendar's popups, under
   * columns that say "Tue 20", where "Tomorrow" read as a different day (Petr, 2026-10-19).
   */
  weekdays?: boolean;
};

/** The long size without a time: a relative day leads and its date follows; a date leads and how far follows. */
const longParts = (days: number, day: Moment, now: Moment) => {
  const date = formatDate(day, now);
  if (days >= 7) return { strong: `${day.format('dddd')}, ${date}`, soft: t.IN_DAYS(days) };
  if (days <= -7) return { strong: `${day.format('dddd')}, ${date}`, soft: t.DAYS_AGO(-days) };
  // "3 days ago · Sat, Oct 17": a weekday alone would read as the coming one.
  if (days < -1) return { strong: t.DAYS_AGO(-days), soft: `${day.format('ddd')}, ${date}` };
  return { strong: dayWord(days, day, now, 'dddd'), soft: date };
};

/** A due date in both sizes. */
export const dueDayLabel = (
  dueDate: DateInput,
  { now = moment(), withoutTime = false, keepTime = false, weekdays = false }: Options = {}
): DayLabel => {
  const at = moment(dueDate);
  const day = deadlineDay(at);
  const days = daysFrom(day, now);
  const time = formatTime(at);
  const word = dayWord(days, day, now, 'ddd', weekdays);
  // Cards give the time only for the coming days, and only when it isn't the day's end.
  const short = keepTime
    ? `${word}${Math.abs(days) >= 7 ? ',' : ''} ${time}`
    : days >= 0 && days < 7 && !isEndOfDay(at)
    ? `${word} ${time}`
    : word;
  const { strong, soft } = longParts(days, day, now);
  // Popups give the time from today on; a day that has passed needs none.
  const timed = !withoutTime && days >= 0 ? `${strong}${days >= 7 ? ',' : ''} ${time}` : strong;
  return { short, strong: timed, soft, days, overdue: at.isBefore(now) };
};

/**
 * A study session that has passed, as Auto-plan's missed list gives it (Petr, 2026-10-19): "Today 8:00am – 8:30am",
 * "Yesterday …", then "3 days ago, …" as an overdue task counts back, and from a week back the date, "Oct 10, …".
 */
export const pastSessionLabel = (start: DateInput, end: DateInput, now: Moment = moment()) => {
  const from = moment(start);
  const day = from.clone().startOf('day');
  const days = daysFrom(day, now);
  const word =
    days === 0
      ? t.TODAY
      : days === -1
      ? t.YESTERDAY
      : days < -1 && days > -7
      ? `${t.DAYS_AGO(-days)},`
      : `${formatDate(day, now)},`;
  return `${word} ${from.format(timeFormat())} – ${moment(end).format(timeFormat())}`;
};

/** A day as a card's Start line gives it ("Today", "Sat", "Oct 28"), or nothing once it has passed. */
export const upcomingDayLabel = (day: DateInput, now: Moment = moment(), weekdays = false) => {
  const start = moment(day).startOf('day');
  const days = daysFrom(start, now);
  return days < 0 ? '' : dayWord(days, start, now, 'ddd', weekdays);
};

/**
 * When to start, `daysAhead` days before the due date (the start of that day, as Auto-plan reads it). `short` is empty
 * once the day has passed: a card's Start line only ever says "not yet".
 */
export const startDayLabel = (
  dueDate: DateInput,
  daysAhead: number,
  { now = moment(), weekdays = false }: Options = {}
): DayLabel => {
  const day = moment(dueDate)
    .startOf('day')
    .subtract(daysAhead, 'days');
  const days = daysFrom(day, now);
  return { short: upcomingDayLabel(day, now, weekdays), ...longParts(days, day, now), days, overdue: false };
};

/** A week's heading from its first day: "This week · Oct 19 – 25", "Next week · Oct 26 – Nov 1", or just "Nov 2 – 8". */
export const weekLabel = (weekStart: DateInput, now: Moment = moment()) => {
  const start = moment(weekStart).startOf('day');
  const end = start.clone().add(6, 'days');
  const format = shortDateFormat();
  // The token stands for the student's own order: "D MMM" puts the month at the end ("19 – 25 Oct").
  const dayFirst = /^D/.test((moment.localeData() as any).longDateFormat(format) || format);
  let range: string;
  if (start.month() !== end.month()) {
    range = `${start.format(format)} – ${end.format(format)}`;
  } else if (dayFirst) {
    range = `${start.format('D')} – ${end.format(format)}`;
  } else {
    range = `${start.format(format)} – ${end.format('D')}`;
  }
  const weeks = Math.round(start.diff(now.clone().startOf('week'), 'weeks', true));
  const name = weeks === 0 ? t.THIS_WEEK : weeks === 1 ? t.NEXT_WEEK : '';
  return { name, range };
};

/** A sidebar heading for the day itself (a group's day, not a deadline): "Thursday · Oct 22", "Wednesday, Oct 28 · in 9 days". */
export const dayHeadingLabel = (day: DateInput, now: Moment = moment()) => {
  const start = moment(day).startOf('day');
  return longParts(daysFrom(start, now), start, now);
};

/**
 * A task card's two lines: when it's due ("Thu 10:00") and, while that day is still ahead, when to start ("Tomorrow").
 * A start of 0 days is the due day itself, which the Due line already gives.
 */
export const cardDates = (
  dueDate?: DateInput | null,
  daysAhead?: number | null,
  { now = moment(), weekdays = false }: Pick<Options, 'now' | 'weekdays'> = {}
) => ({
  due: dueDate ? dueDayLabel(dueDate, { now, weekdays }).short : '',
  start: dueDate && daysAhead && daysAhead > 0 ? startDayLabel(dueDate, daysAhead, { now, weekdays }).short : ''
});
